/* eslint-disable complexity */
import '../../tables/era-eob/neb-table-era-eob-charges';

import { openPopup } from '@neb/popup';
import { html, css, LitElement } from 'lit';

import { getBillingCodesWriteOffs } from '../../../../packages/neb-api-client/src/billing-codes';
import { formatAdjustments } from '../../../../packages/neb-api-client/src/formatters/line-item-details';
import { getPatientInsurances } from '../../../../packages/neb-api-client/src/patient-insurance-api-client';
import { POPUP_RENDER_KEYS } from '../../../../packages/neb-popup/src/renderer-keys';
import { CSS_SPACING } from '../../../../packages/neb-styles/neb-variables';
import {
  CODE_PAYMENTS,
  CODE_WRITE_OFFS,
} from '../../../../packages/neb-utils/constants';
import { fetchPaymentTypeItems } from '../../../../packages/neb-utils/neb-ledger-util';
import { updateEraEob } from '../../../api-clients/era-eob';

import { calculateRemainder } from './neb-charges-spreadsheet-controller-util';

export const ELEMENTS = {
  chargesTable: { id: 'charges-table' },
  saveButton: { id: 'save-button' },
};

function isOtherPayerDebit(lineItemDebit, item) {
  return (
    lineItemDebit.payerId &&
    lineItemDebit.payerId !== item.eraPayerId &&
    (!lineItemDebit.payerGroupId ||
      lineItemDebit.payerGroupId !== item.eraPayerGroupId)
  );
}

function isPayerDebit(lineItemDebit, item) {
  return (
    lineItemDebit.payerId === item.eraPayerId ||
    (lineItemDebit.payerGroupId &&
      lineItemDebit.payerGroupId === item.eraPayerGroupId)
  );
}

function isDebitOfInsurance(debit, insurance) {
  return (
    debit.payerId === insurance.data.payerPlanId ||
    (debit.payerGroupId &&
      debit.payerGroupId === insurance.data.payerPlan.payerGroupId)
  );
}

function filterPayerLineItemDebits(item) {
  return item.lineItemDebits
    .filter(lineItemDebit => isPayerDebit(lineItemDebit, item))
    .map(lineItemDebit => ({
      amount: lineItemDebit.amount,
      allocations: lineItemDebit.allocations,
      codePaymentId: lineItemDebit.codePaymentId,
      patientInsuranceId: lineItemDebit.patientInsuranceId,
      payerId: lineItemDebit.payerId,
      id: lineItemDebit.lineItemDebitId,
      payerGroupId: lineItemDebit.payerGroupId,
    }));
}

function filterOtherPayerLineItemDebits(item) {
  return item.lineItemDebits
    .filter(lineItemDebit => isOtherPayerDebit(lineItemDebit, item))
    .map(lineItemDebit => ({
      amount: lineItemDebit.amount,
      allocations: lineItemDebit.allocations,
      codePaymentId: lineItemDebit.codePaymentId,
      patientInsuranceId: lineItemDebit.patientInsuranceId,
      payerId: lineItemDebit.payerId,
      id: lineItemDebit.lineItemDebitId,
      payerGroupId: lineItemDebit.payerGroupId,
    }));
}

function filterPatientLineItemDebits(item) {
  return item.lineItemDebits
    .filter(lineItemDebit => lineItemDebit.payerId === null)
    .map(lineItemDebit => ({
      debit: {
        amount: lineItemDebit.amount,
        allocations: lineItemDebit.allocations,
      },
      codePaymentId: lineItemDebit.codePaymentId,
    }));
}

const CONFIG = [
  { key: 'patientName', label: 'Patient', numeric: false, editable: false },
  { key: 'code', label: 'Code', numeric: false, editable: false },
  { key: 'dateOfService', label: 'DOS', numeric: false, editable: false },
  {
    key: 'description',
    label: 'Description',
    numeric: false,
    editable: false,
  },
  { key: 'billedAmount', label: 'Billed', numeric: true, editable: false },
  { key: 'allowedAmount', label: 'Allowed', numeric: true, editable: false },
  {
    key: 'otherPaymentsAmount',
    label: 'Other Paid',
    numeric: true,
    popup: true,
    editable: true,
    getReadOnly: item => item.otherPayments.length === 0,
    getForcePopup: () => true,
  },
  { key: 'paid', label: 'Paid', numeric: true, editable: true },
  {
    key: 'outstanding',
    label: 'Outstanding',
    numeric: true,
    editable: true,
    popup: true,
    getForcePopup: () => true,
  },
  {
    key: 'otherPayerDebits',
    label: 'Other Resp.',
    numeric: true,
    popup: true,
    editable: true,
    getReadOnly: item =>
      (item.secondaryPayerId === null ||
        item.secondaryPayerId === item.primaryPayerId) &&
      filterOtherPayerLineItemDebits(item).length === 0,
    getForcePopup: item => filterOtherPayerLineItemDebits(item).length > 1,
  },
  {
    key: 'patient',
    label: 'Patient Owed',
    numeric: true,
    editable: true,
    popup: true,
    getForcePopup: item => filterPatientLineItemDebits(item).length > 1,
  },
  {
    key: 'adjustment',
    label: 'Adjustment',
    numeric: true,
    editable: true,
    popup: true,
    allowNegative: true,
    getForcePopup: item => item.adjustments?.length > 1,
  },
  {
    key: 'remainder',
    label: 'Remainder',
    numeric: true,
    editable: false,
    calculate: item => calculateRemainder(item),
  },
];

function getNewOtherLineItemDebit(
  lineItem,
  payerId,
  patientInsuranceId,
  codePaymentId = CODE_PAYMENTS['PR-45'].id,
  amount = lineItem.otherPayerDebits,
  allocations = [],
  lineItemDebitId = null,
  payerGroupId = null,
) {
  return {
    codePaymentId,
    amount,
    lineItemId: lineItem.id,
    payerId,
    patientInsuranceId,
    allocations,
    lineItemDebitId,
    payerGroupId,
  };
}

function getNewOutstandingLineItemDebit(
  lineItem,
  payerId,
  patientInsuranceId,
  codePaymentId,
  amount = lineItem.otherPayerDebits,
  allocations = [],
  lineItemDebitId = null,
  payerGroupId = null,
) {
  return {
    codePaymentId,
    amount,
    lineItemId: lineItem.id,
    payerId,
    patientInsuranceId,
    allocations,
    lineItemDebitId,
    payerGroupId,
  };
}

function getNewPatientLineItemDebit(
  lineItem,
  codePaymentId = CODE_PAYMENTS.GENERAL_PAYMENT.id,
  amount = lineItem.patient,
  lineItemDebit = null,
  lineItemDebitId = null,
) {
  return {
    codePaymentId,
    amount,
    lineItemId: lineItem.id,
    payerId: null,
    patientInsuranceId: null,
    allocations: lineItemDebit ? lineItemDebit.allocations : [],
    lineItemDebitId,
  };
}

function getNewAdjustment(
  lineItem,
  codeId = CODE_WRITE_OFFS.NEGOTIATED_RATE.id,
  amount = lineItem.adjustment,
  id = null,
) {
  return { amount, codeId, id, lineItemId: lineItem.id };
}

function wasDebitChanged(initialDebits, resultDebits) {
  return (
    initialDebits.length !== resultDebits.length ||
    initialDebits.some((initialDebit, index) => {
      const resultDebit = resultDebits[index];

      const initialAmount = initialDebit.debit
        ? initialDebit.debit.amount
        : initialDebit.amount;

      return (
        initialAmount !== resultDebit.amount ||
        initialDebit.codePaymentId !== resultDebit.codePaymentId
      );
    })
  );
}

function calculateOutstanding(lineItem) {
  const payerDebits = filterPayerLineItemDebits(lineItem);

  const newOutstanding = payerDebits.reduce((acc, debit) => {
    const allocationSum = debit.allocations?.reduce((sum, allocation) => {
      if (!allocation.belongsToEraEob) {
        return sum + allocation.amount;
      }

      return sum;
    }, 0);

    return acc + debit.amount - allocationSum;
  }, 0);

  return newOutstanding - lineItem.paid;
}

class NebChargesSpreadsheetController extends LitElement {
  static get properties() {
    return {
      charges: { type: Array },
      eraEobId: { type: String },
      reportId: { type: String },
      isSaving: { type: Boolean },
      saveSuccess: { type: Boolean },
      saveError: { type: String },
      disableSave: { type: Boolean },
      paymentId: { type: String },
      payerPlanId: { type: String },
      filteredLineItemIds: { type: Array },
      resetTable: { type: Boolean },
      type: { type: String },
      disableTable: { type: Boolean },
      __patientInsurances: { type: Map },
      __itemsWithRemainderErrors: { type: Set },
      __filteredCharges: { type: Array },
      __isPopupLoading: { type: Boolean },
    };
  }

  static get styles() {
    return css`
      .container {
        padding: 0 ${CSS_SPACING} ${CSS_SPACING} ${CSS_SPACING};
        background: white;
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
      }

      .save-button {
        background-color: #0078d4;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 8px 16px;
        font-size: 14px;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .save-button:hover {
        background-color: #0063b1;
      }

      .save-button:disabled {
        background-color: #ccc;
        cursor: not-allowed;
      }

      .status-message {
        margin-right: 16px;
        display: flex;
        align-items: center;
      }

      .success {
        color: #107c10;
      }

      .error {
        color: #d83b01;
      }
    `;
  }

  constructor() {
    super();
    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.charges = [];
    this.eraEobId = '';
    this.reportId = '';
    this.isSaving = false;
    this.saveSuccess = false;
    this.saveError = '';
    this.disableSave = false;
    this.paymentId = '';
    this.payerPlanId = '';
    this.filteredLineItemIds = [];
    this.resetTable = false;
    this.type = '';
    this.disableTable = false;

    this.__statusTimer = null;
    this.__itemsWithRemainderErrors = new Set();
    this.__patientInsurances = new Map();
    this.__isPopupLoading = false;
    this.__originalCharges = null;
    this.__originalDebits = new Map(); // Track original debit states for reversibility
    this.__manuallyEditedItems = new Set(); // Track items where popup was opened

    this.onUpdate = () => {};

    this.onSave = () => {};

    this.onUpdateModel = () => {};

    this.onRemainderChange = () => {};
  }

  __initHandlers() {
    this.handlers = {
      handleUpdate: async (item, field) => {
        if (field === 'paid') {
          // Only adjust debits automatically if not in manual mode
          if (!this.__manuallyEditedItems.has(item.id)) {
            this.__adjustDebitsForPaidChange(item, true);
          }

          const payerDebits = filterPayerLineItemDebits(item);

          if (payerDebits.length === 0) {
            const primaryPayerMatches = item.primaryPayerId === item.eraPayerId;

            const secondaryPayerMatches =
              item.secondaryPayerId === item.eraPayerId;

            let patientInsuranceId = null;

            if (primaryPayerMatches) {
              patientInsuranceId = item.primaryInsuranceId;
            } else if (secondaryPayerMatches) {
              patientInsuranceId = item.secondaryInsuranceId;
            }

            if (!patientInsuranceId) {
              const patientInsurances = await this.__fetchPatientInsurances(
                item.patientId,
              );

              const relevantPatientInsurance = patientInsurances.find(
                insurance => insurance.data.payerPlanId === item.eraPayerId,
              );

              patientInsuranceId = relevantPatientInsurance?.data?.id || null;
            }

            const codePaymentId = primaryPayerMatches
              ? null
              : CODE_PAYMENTS['PR-45'].id;

            item.lineItemDebits.push(
              getNewOutstandingLineItemDebit(
                item,
                item.eraPayerId,
                patientInsuranceId,
                codePaymentId,
                item.paid,
                [],
                null,
                item.eraPayerGroupId,
              ),
            );
          }

          item.outstanding = calculateOutstanding(item);
        }

        if (field === 'otherPayerDebits') {
          const otherPayerLineItemDebit = item.lineItemDebits.find(debit =>
            isOtherPayerDebit(debit, item),
          );

          if (otherPayerLineItemDebit) {
            otherPayerLineItemDebit.amount = item.otherPayerDebits;
          } else {
            const otherPayerId =
              item.eraPayerId === item.secondaryPayerId
                ? item.primaryPayerId
                : item.secondaryPayerId;

            const otherPayerInsuranceId =
              otherPayerId === item.primaryPayerId
                ? item.primaryInsuranceId
                : item.secondaryInsuranceId;

            item.lineItemDebits.push(
              getNewOtherLineItemDebit(
                item,
                otherPayerId,
                otherPayerInsuranceId,
              ),
            );
          }
        } else if (field === 'patient') {
          const patientLineItemDebit = item.lineItemDebits.find(
            debit => debit.payerId === null && debit.codePaymentId,
          );

          if (patientLineItemDebit) {
            patientLineItemDebit.amount = item.patient;
          } else {
            item.lineItemDebits.push(getNewPatientLineItemDebit(item));
          }
        } else if (field === 'adjustment') {
          if (!item.adjustments) {
            item.adjustments = [];
          }

          const adjustment = item.adjustments[0];

          if (adjustment) {
            adjustment.amount = item.adjustment;
          } else {
            item.adjustments.push(getNewAdjustment(item));
          }
        }

        this.__updateRemainderError(item);

        const itemIndex = this.charges.findIndex(c => c.id === item.id);

        this.charges[itemIndex] = item;
        this.onUpdate(this.charges);
      },
      handleRemainderChange: (item, hasRemainderError) => {
        const newSet = new Set(this.__itemsWithRemainderErrors);

        if (hasRemainderError) {
          newSet.add(item.id);
        } else {
          newSet.delete(item.id);
        }

        this.__itemsWithRemainderErrors = newSet;
        this.onRemainderChange(item, hasRemainderError);
      },
      handleOpenPopup: async (item, key) => {
        if (this.__isPopupLoading) {
          return item[key];
        }

        this.__isPopupLoading = true;

        await this.__fetchPopupData(item, key);

        const lineItem = this.charges.find(c => c.id === item.id);

        let wasFieldChanged = false;

        if (key === 'outstanding') {
          wasFieldChanged = await this.__openOutstandingPopup(lineItem);
        } else if (key === 'otherPayerDebits') {
          wasFieldChanged = await this.__openOtherPayerDebitPopup(lineItem);
        } else if (key === 'patient') {
          wasFieldChanged = await this.__openPatientDebitPopup(lineItem);
        } else if (key === 'adjustment') {
          wasFieldChanged = await this.__openAdjustmentPopup(lineItem);
        } else if (key === 'otherPaymentsAmount') {
          await this.__openOtherPayerPaymentsPopup(lineItem);
        }

        // Update remainder error after popup changes
        if (wasFieldChanged !== false) {
          this.__updateRemainderError(lineItem);
        }

        this.charges = [...this.charges];
        this.onUpdate(this.charges);

        this.__isPopupLoading = false;

        return wasFieldChanged;
      },
      handleSave: async () => {
        if (!this.eraEobId) {
          this.saveError = 'Missing ERA/EOB ID';
          this.showStatusMessage();
          return;
        }

        try {
          this.isSaving = true;
          this.saveSuccess = false;
          this.saveError = '';

          if (this.__statusTimer) {
            clearTimeout(this.__statusTimer);
          }

          // Get the table component to access edited cells
          const tableElement = this.shadowRoot.querySelector('#charges-table');
          const editedCells = tableElement.__editedCells || {};

          // Filter charges to only include those that have been edited
          const editedCharges = this.charges.filter(
            charge =>
              editedCells[charge.id] &&
              calculateRemainder(charge) === 0 &&
              charge.outstanding >= 0,
          );

          if (editedCharges.length === 0) {
            this.saveError = 'No valid changes to save';
            this.showStatusMessage();
            return;
          }

          const body = {
            payerId: this.payerId,
            paymentId: this.paymentId,
            payerGroupId: editedCharges[0].eraPayerGroupId,
            lineItems: editedCharges.map(charge => ({
              id: charge.id,
              paid: charge.paid,
              debits: charge.lineItemDebits.map(lid => ({
                id: lid.debitId || null,
                owedAmount: lid.amount,
                patientInsuranceId: lid.patientInsuranceId || null,
                payerId: lid.payerId,
                codePaymentId: lid.codePaymentId || null,
                payerGroupId: lid.payerGroupId || null,
              })),
              adjustments: formatAdjustments(charge),
            })),
          };

          await updateEraEob({
            body,
            id: this.eraEobId,
          });

          this.saveSuccess = true;
          this.showStatusMessage();

          // Clear the edited cells tracking after successful save
          tableElement.__editedCells = {};

          // Reset state to treat current state as new "original" state
          this.__resetStateAfterSave();

          // Call the onSave callback to refresh data
          this.onSave();
        } catch (error) {
          this.saveError = 'Failed to save changes';
          this.showStatusMessage();
        } finally {
          this.isSaving = false;
        }
      },
    };
  }

  __handleResetTable() {
    const table = this.shadowRoot.getElementById(ELEMENTS.chargesTable.id);

    table.resetEditedCells();
    this.__resetStateAfterSave();
  }

  __resetStateAfterSave() {
    // Clear manual editing tracking - all items now start fresh
    this.__manuallyEditedItems.clear();

    // Clear original debits tracking - current debits become new "original"
    this.__originalDebits.clear();

    // Update original charges to current state - current charges become new "original"
    this.__originalCharges = structuredClone(this.charges);
  }

  __storeOriginalDebits(item) {
    if (!this.__originalDebits.has(item.id)) {
      const payerDebits = filterPayerLineItemDebits(item);

      if (payerDebits.length === 0) {
        const originalZeroDebit = {
          amount: 0,
          codePaymentId: null,
          payerId: item.eraPayerId,
          patientInsuranceId: null,
          id: null,
          allocations: [],
        };

        this.__originalDebits.set(item.id, [originalZeroDebit]);
      } else {
        this.__originalDebits.set(item.id, structuredClone(payerDebits));
      }
    }
  }

  __adjustDebitsForPaidChange(item, isZeroing = false) {
    // Don't auto-adjust if user has manually edited debits via popup
    if (this.__manuallyEditedItems.has(item.id) && !isZeroing) {
      return;
    }

    this.__storeOriginalDebits(item);

    const originalCharge = this.__originalCharges?.find(c => c.id === item.id);
    const originalOutstanding = originalCharge?.outstanding || 0;

    // Calculate the effective amount this payment needs to cover
    // (original debit sum minus what's already paid by other payments)
    const originalDebits = this.__originalDebits.get(item.id) || [];
    const originalDebitSum = originalDebits.reduce(
      (sum, debit) => sum + debit.amount,
      0,
    );
    const otherPaymentAllocationsTotal = originalDebits.reduce(
      (total, debit) => {
        const otherAllocations =
          debit.allocations?.reduce(
            (sum, allocation) =>
              allocation.belongsToEraEob ? sum : sum + allocation.amount,
            0,
          ) || 0;
        return total + otherAllocations;
      },
      0,
    );

    const effectiveDebitAmount =
      originalDebitSum - otherPaymentAllocationsTotal;

    this.__restoreDebitsToOriginal(item);

    // When paid is below the effective amount, debits should sum to: currentPaid + originalOutstanding
    if (item.paid < effectiveDebitAmount) {
      const targetDebitSum = isZeroing
        ? item.paid
        : item.paid + originalOutstanding;

      this.__adjustDebitsToTargetSum(item, targetDebitSum);
    }
    // When paid reaches or exceeds effective amount, restore to original and add excess to first debit
    else if (item.paid >= effectiveDebitAmount) {
      // If paid exceeds effective amount, add the excess to the first debit
      const excessAmount = item.paid - effectiveDebitAmount;

      if (excessAmount > 0) {
        this.__addExcessToFirstDebit(item, excessAmount);
      }
    }
  }

  __adjustDebitsToTargetSum(item, targetSum) {
    const payerDebits = item.lineItemDebits.filter(debit =>
      isPayerDebit(debit, item),
    );

    // Calculate total allocations from OTHER payments
    const otherPaymentAllocationsTotal = payerDebits.reduce((total, debit) => {
      const otherAllocations =
        debit.allocations?.reduce(
          (sum, allocation) =>
            allocation.belongsToEraEob ? sum : sum + allocation.amount,
          0,
        ) || 0;
      return total + otherAllocations;
    }, 0);

    // Adjust target sum to account for other payment allocations
    const adjustedTargetSum = targetSum + otherPaymentAllocationsTotal;

    const currentSum = payerDebits.reduce(
      (sum, debit) => sum + debit.amount,
      0,
    );

    const difference = adjustedTargetSum - currentSum;

    if (difference < 0) {
      // Need to reduce debits
      this.__reduceDebitAmounts(payerDebits, Math.abs(difference));
    } else if (difference > 0) {
      // Need to increase debits
      this.__increaseDebitAmounts(payerDebits, difference, item);
    }
  }

  __restoreDebitsToOriginal(item) {
    const originalDebits = this.__originalDebits.get(item.id);
    if (!originalDebits) return;

    const payerDebits = item.lineItemDebits.filter(debit =>
      isPayerDebit(debit, item),
    );

    // Restore each debit to its original amount
    payerDebits.forEach(currentDebit => {
      const originalDebit = originalDebits.find(
        orig => orig.id === currentDebit.lineItemDebitId,
      );

      if (originalDebit) {
        currentDebit.amount = originalDebit.amount;
      }
    });
  }

  __addExcessToFirstDebit(item, excessAmount) {
    const payerDebits = item.lineItemDebits.filter(debit =>
      isPayerDebit(debit, item),
    );

    if (payerDebits.length > 0) {
      // Add excess to the first debit in array order
      payerDebits[0].amount += excessAmount;
    }
  }

  __reduceDebitAmounts(payerDebits, reductionAmount) {
    let remainingReduction = reductionAmount;

    // Reduce debits in forward order (bucket by bucket)
    // First prioritize debits with allocations from this payment, then others
    const debitsWithAllocations = payerDebits.filter(debit =>
      debit.allocations?.some(alloc => alloc.belongsToEraEob),
    );
    const debitsWithoutAllocations = payerDebits.filter(
      debit => !debit.allocations?.some(alloc => alloc.belongsToEraEob),
    );

    // Process in array order: first all debits with allocations, then others
    const orderedDebits = [
      ...debitsWithAllocations,
      ...debitsWithoutAllocations,
    ];

    orderedDebits.some(debit => {
      if (remainingReduction <= 0) return true; // break out of loop

      // Calculate how much of this debit is already paid by OTHER payments
      const otherPaymentAllocations =
        debit.allocations?.reduce(
          (sum, allocation) =>
            allocation.belongsToEraEob ? sum : sum + allocation.amount,
          0,
        ) || 0;

      // Available amount we can reduce = debit amount minus what's already paid by other payments
      const availableToReduce = Math.max(
        0,
        debit.amount - otherPaymentAllocations,
      );

      // Only reduce up to the available amount
      const reductionForThisDebit = Math.min(
        availableToReduce,
        remainingReduction,
      );

      if (reductionForThisDebit > 0) {
        debit.amount -= reductionForThisDebit;
        remainingReduction -= reductionForThisDebit;
      }

      return false; // continue loop
    });
  }

  __increaseDebitAmounts(payerDebits, increaseAmount, item) {
    let remainingIncrease = increaseAmount;

    const originalDebits = this.__originalDebits.get(item.id);

    if (!originalDebits) {
      // Fallback to proportional distribution if no original debits stored
      return this.__distributeIncreaseProportionally(
        payerDebits,
        increaseAmount,
      );
    }

    // Restore debits in REVERSE order from reduction (last reduced = first restored)
    // Reduction order: debitsWithAllocations[0..n], debitsWithoutAllocations[0..n]
    // Restoration order: debitsWithoutAllocations[n..0], debitsWithAllocations[n..0]
    const debitsWithAllocations = payerDebits.filter(debit =>
      debit.allocations?.some(alloc => alloc.belongsToEraEob),
    );
    const debitsWithoutAllocations = payerDebits.filter(
      debit => !debit.allocations?.some(alloc => alloc.belongsToEraEob),
    );

    // Process in exact reverse order: last debitsWithoutAllocations first, then last debitsWithAllocations
    const orderedDebits = [
      ...debitsWithoutAllocations.slice().reverse(),
      ...debitsWithAllocations.slice().reverse(),
    ];

    // Restore debits to their original amounts one by one (bucket by bucket)
    orderedDebits.some(currentDebit => {
      if (remainingIncrease <= 0) return true; // break out of loop

      const originalDebit = originalDebits.find(
        orig => orig.lineItemDebitId === currentDebit.lineItemDebitId,
      );

      if (originalDebit && currentDebit.amount < originalDebit.amount) {
        // Calculate how much we can restore (up to original amount)
        const maxRestore = originalDebit.amount - currentDebit.amount;
        const restoreAmount = Math.min(maxRestore, remainingIncrease);

        currentDebit.amount += restoreAmount;
        remainingIncrease -= restoreAmount;
      }

      return false; // continue loop
    });

    return undefined;
  }

  __distributeIncreaseProportionally(payerDebits, increaseAmount) {
    let remainingIncrease = increaseAmount;

    // Sort debits by amount (lowest first for increasing)
    const sortedDebits = [...payerDebits].sort((a, b) => a.amount - b.amount);

    sortedDebits.some(debit => {
      if (remainingIncrease <= 0) return true; // break out of loop

      // Increase this debit by the remaining amount or proportionally
      const increaseForThisDebit = Math.min(
        remainingIncrease,
        remainingIncrease / sortedDebits.length, // Distribute evenly
      );
      debit.amount += increaseForThisDebit;
      remainingIncrease -= increaseForThisDebit;

      return false; // continue loop
    });

    // If there's still remaining increase, add it to the first debit
    if (remainingIncrease > 0 && sortedDebits.length > 0) {
      sortedDebits[0].amount += remainingIncrease;
    }
  }

  __updateRemainderError(item) {
    const hasRemainderError = calculateRemainder(item) !== 0;
    const newSet = new Set(this.__itemsWithRemainderErrors);

    if (hasRemainderError) {
      newSet.add(item.id);
    } else {
      newSet.delete(item.id);
    }

    this.__itemsWithRemainderErrors = newSet;
    this.onRemainderChange(item, hasRemainderError);
  }

  async __fetchPatientInsurances(patientId) {
    if (this.__patientInsurances.has(patientId)) {
      return this.__patientInsurances.get(patientId);
    }

    const insurances = await getPatientInsurances(patientId);

    const formattedInsurances = insurances.map(insurance => ({
      data: insurance,
      label: `${insurance.planName} - ${insurance.payerPlan.alias}`,
    }));

    this.__patientInsurances.set(patientId, formattedInsurances);

    return formattedInsurances;
  }

  async __fetchPopupData(item, key) {
    const fetchPromises = [];

    const needsPaymentCodes =
      key === 'otherPayerDebits' || key === 'patient' || key === 'outstanding';
    const needsCodeWriteOffs = key === 'adjustment';
    const needsPatientInsurances =
      key === 'otherPayerDebits' || key === 'outstanding';

    if (needsPaymentCodes && !this.__paymentCodes) {
      fetchPromises.push(
        fetchPaymentTypeItems().then(codes => {
          this.__paymentCodes = codes;
        }),
      );
    }

    if (needsCodeWriteOffs && !this.__codeWriteOffs) {
      fetchPromises.push(
        getBillingCodesWriteOffs().then(codes => {
          this.__codeWriteOffs = codes;
        }),
      );
    }

    if (
      needsPatientInsurances &&
      !this.__patientInsurances.has(item.patientId)
    ) {
      fetchPromises.push(
        getPatientInsurances(item.patientId).then(insurances => {
          const formattedInsurances = insurances.map(insurance => ({
            data: insurance,
            label: `${insurance.planName} - ${insurance.payerPlan.alias}`,
          }));

          this.__patientInsurances.set(item.patientId, formattedInsurances);
        }),
      );
    }

    if (fetchPromises.length > 0) {
      await Promise.all(fetchPromises);
    }
  }

  async __openAdjustmentPopup(lineItem) {
    const initialAdjustments = lineItem.adjustments;

    const result = await openPopup(
      POPUP_RENDER_KEYS.INDIVIDUAL_CODE_AND_AMOUNT,
      {
        title: 'Adjustments',
        initialCodes: initialAdjustments,
        displayPaidAmount: false,
        billType: lineItem.billType,
        codeWriteOffs: this.__codeWriteOffs,
      },
    );

    if (!result || !result.popupChanged) return false;

    lineItem.adjustments = [...result.itemsToEdit, ...result.itemsToAdd].map(
      adjustment =>
        getNewAdjustment(
          lineItem,
          adjustment.codeId,
          adjustment.amount,
          result.individualRowId,
        ),
    );

    lineItem.adjustment = lineItem.adjustments.reduce(
      (acc, adjustment) => acc + adjustment.amount,
      0,
    );

    const wasAdjustmentChanged =
      initialAdjustments.length !== lineItem.adjustments.length ||
      initialAdjustments.some((initialAdjustment, index) => {
        const resultAdjustment = lineItem.adjustments[index];

        return (
          initialAdjustment.amount !== resultAdjustment.amount ||
          initialAdjustment.codeId !== resultAdjustment.codeId
        );
      });

    if (wasAdjustmentChanged) {
      return lineItem.adjustment;
    }

    return false;
  }

  async __openPatientDebitPopup(lineItem) {
    const initialCodes = filterPatientLineItemDebits(lineItem);

    const result = await openPopup(
      POPUP_RENDER_KEYS.INDIVIDUAL_CODE_AND_AMOUNT,
      {
        title: 'Patient Responsibility',
        initialCodes,
        displayPaidAmount: true,
        paymentCodes: this.__paymentCodes,
        billType: lineItem.billType,
      },
    );

    if (!result || !result.popupChanged) return false;

    const resultItems = [...result.itemsToEdit, ...result.itemsToAdd].map(
      resultItem => {
        const matchedLineItemDebit = lineItem.lineItemDebits.find(
          ({ lineItemDebitId }) =>
            lineItemDebitId === resultItem.individualRowId,
        );

        return getNewPatientLineItemDebit(
          lineItem,
          resultItem.codeId,
          resultItem.amount,
          matchedLineItemDebit,
          resultItem.individualRowId,
        );
      },
    );

    lineItem.lineItemDebits = [
      ...lineItem.lineItemDebits.filter(debit => debit.payerId !== null),
      ...resultItems,
    ];

    lineItem.patient = resultItems.reduce((acc, { amount }) => acc + amount, 0);

    if (wasDebitChanged(initialCodes, resultItems)) {
      return lineItem.patient;
    }

    return false;
  }

  async __openOutstandingPopup(lineItem) {
    const initialDebits = filterPayerLineItemDebits(lineItem);

    const filteredPatientInsurances = this.__patientInsurances
      .get(lineItem.patientId)
      .filter(
        insurance =>
          insurance.data.payerPlanId === lineItem.eraPayerId ||
          initialDebits.some(debit => isDebitOfInsurance(debit, insurance)),
      );

    const result = await openPopup(POPUP_RENDER_KEYS.OTHER_RESPONSIBILITY, {
      title: 'Outstanding Payer Responsibility',
      initialDebits,
      patientInsurances: filteredPatientInsurances,
      paymentCodes: this.__paymentCodes,
      disablePrimaryType: lineItem.primaryPayerId !== lineItem.eraPayerId,
      primaryInsuranceId: lineItem.primaryInsuranceId,
    });

    if (!result || !result.popupChanged) return false;

    const newResultItems = [...result.itemsToEdit, ...result.itemsToAdd].map(
      resultItem => {
        const payerGroupId = filteredPatientInsurances.find(
          insurance => insurance.data.payerPlanId === resultItem.payerId,
        )?.data.payerPlan.payerGroupId;

        return getNewOutstandingLineItemDebit(
          lineItem,
          resultItem.payerId,
          resultItem.patientInsuranceId,
          resultItem.codeId,
          resultItem.amount,
          resultItem.allocations,
          resultItem.individualRowId,
          payerGroupId,
        );
      },
    );

    lineItem.lineItemDebits = [
      ...lineItem.lineItemDebits.filter(
        debit => !isPayerDebit(debit, lineItem),
      ),
      ...newResultItems,
    ];

    lineItem.outstanding = calculateOutstanding(lineItem);

    if (wasDebitChanged(initialDebits, newResultItems)) {
      // Mark this item as manually edited to stop auto-adjusting debits
      this.__manuallyEditedItems.add(lineItem.id);
      return lineItem.outstanding;
    }

    return false;
  }

  async __openOtherPayerPaymentsPopup(lineItem) {
    const initialPayments = structuredClone(lineItem.otherPayments);

    const result = await openPopup(POPUP_RENDER_KEYS.OTHER_PAYMENTS, {
      title: 'Other Paid Payer Payments',
      payments: lineItem.otherPayments,
      paymentId: this.paymentId,
      dirty: this.dirty,
    });

    if (!result) return;

    const initialPaymentMap = new Map(
      initialPayments.map(payment => [
        `${payment.id}-${payment.allocationId}`,
        payment,
      ]),
    );

    const recoupedPayments = result.filter(payment => {
      const initialPayment = initialPaymentMap.get(
        `${payment.id}-${payment.allocationId}`,
      );

      return (
        initialPayment?.isAllocationValid &&
        !payment.isAllocationValid &&
        payment.recoupId
      );
    });

    if (recoupedPayments.length > 0) {
      this.onUpdateModel();
    } else {
      lineItem.otherPayments = [...result];
    }
  }

  async __openOtherPayerDebitPopup(lineItem) {
    const initialDebits = filterOtherPayerLineItemDebits(lineItem);

    const filteredPatientInsurances = this.__patientInsurances
      .get(lineItem.patientId)
      .filter(insurance => {
        if (insurance.data.payerPlanId !== lineItem.eraPayerId) {
          if (
            insurance.data.payerPlanId === lineItem.primaryPayerId ||
            insurance.data.payerPlanId === lineItem.secondaryPayerId
          ) {
            return true;
          }

          if (
            lineItem.lineItemDebits.some(debit =>
              isDebitOfInsurance(debit, insurance),
            )
          ) {
            return true;
          }

          return false;
        }

        return false;
      });

    const result = await openPopup(POPUP_RENDER_KEYS.OTHER_RESPONSIBILITY, {
      title: 'Other Responsibility',
      initialDebits,
      patientInsurances: filteredPatientInsurances,
      paymentCodes: this.__paymentCodes,
      disablePrimaryType: lineItem.primaryPayerId !== lineItem.eraPayerId,
      primaryInsuranceId: lineItem.primaryInsuranceId,
    });

    if (!result || !result.popupChanged) return false;

    const newResultItems = [...result.itemsToEdit, ...result.itemsToAdd].map(
      resultItem => {
        const payerGroupId = filteredPatientInsurances.find(
          insurance => insurance.data.payerPlanId === resultItem.payerId,
        )?.data.payerPlan.payerGroupId;

        return getNewOtherLineItemDebit(
          lineItem,
          resultItem.payerId,
          resultItem.patientInsuranceId,
          resultItem.codeId,
          resultItem.amount,
          resultItem.allocations,
          resultItem.individualRowId,
          payerGroupId,
        );
      },
    );

    lineItem.lineItemDebits = [
      ...lineItem.lineItemDebits.filter(
        debit => !isOtherPayerDebit(debit, lineItem),
      ),
      ...newResultItems,
    ];

    lineItem.otherPayerDebits = newResultItems.reduce(
      (acc, { amount }) => acc + amount,
      0,
    );

    if (wasDebitChanged(initialDebits, newResultItems)) {
      return lineItem.otherPayerDebits;
    }

    return false;
  }

  firstUpdated() {
    // Check for remainder errors on initial render
    const itemsWithErrors = new Set();

    this.charges.forEach(item => {
      const remainder = calculateRemainder(item);

      if (remainder !== 0) {
        itemsWithErrors.add(item.id);
      }
    });

    this.__itemsWithRemainderErrors = itemsWithErrors;
  }

  update(changedProps) {
    if (
      changedProps.has('charges') &&
      this.charges.length &&
      !this.__originalCharges
    ) {
      this.__originalCharges = structuredClone(this.charges);
    }

    super.update(changedProps);
  }

  updated(changedProps) {
    if (
      changedProps.has('filteredLineItemIds') ||
      changedProps.has('charges')
    ) {
      this.__filteredCharges = this.filteredLineItemIds
        ? this.charges.filter(item =>
            this.filteredLineItemIds.includes(item.id),
          )
        : this.charges;
    }

    if (changedProps.has('resetTable')) {
      this.__handleResetTable();
    }
  }

  showStatusMessage() {
    if (this.__statusTimer) {
      clearTimeout(this.__statusTimer);
    }

    this.__statusTimer = setTimeout(() => {
      this.saveSuccess = false;
      this.saveError = '';
      this.requestUpdate();
    }, 5000);
  }

  get hasRemainderErrors() {
    return this.__itemsWithRemainderErrors.size > 0;
  }

  renderStatusMessage() {
    if (this.saveSuccess) {
      return html`<div class="status-message success">
        Changes saved successfully
      </div>`;
    }

    if (this.saveError) {
      return html`<div class="status-message error">${this.saveError}</div>`;
    }

    return null;
  }

  render() {
    return html`
      <div class="container">
        <neb-table-era-eob-charges
          id="${ELEMENTS.chargesTable.id}"
          .items="${this.__filteredCharges}"
          .config="${CONFIG}"
          .onUpdate="${this.handlers.handleUpdate}"
          .onOpenPopup="${this.handlers.handleOpenPopup}"
          .disabled="${this.__isPopupLoading || this.disableTable}"
        ></neb-table-era-eob-charges>

        <div class="actions">
          ${this.renderStatusMessage()}
          <button
            id="${ELEMENTS.saveButton.id}"
            class="save-button"
            ?disabled="${this.isSaving ||
            !this.charges.length ||
            this.disableSave ||
            this.disableTable}"
            @click="${this.handlers.handleSave}"
          >
            ${this.isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define(
  'neb-charges-spreadsheet-controller',
  NebChargesSpreadsheetController,
);
